/* eslint-disable complexity */
import React, { ReactNode, useEffect } from 'react';

import { css } from '@compiled/react';
import { RootComponentInstance } from '@uniformdev/canvas';
import { AuxiaResponse } from 'src/pages/software/jira/service-management/auxia/get-auxia-jsm-paid-lp-treatment';
import { UniformEntryPage } from 'src/pages/uniform-entry/uniform-entry-page';
import { useWAEContext } from 'src/uniform/utils/wae-context-provider';

import {
	Adg4SectionDivider,
	SECTION_DIVIDER_VARIATION,
} from '@/components/adg4/section-divider/src/section-divider';
import { CarouselProps } from '@/components/confluence/carousel/src/carousel';
import { VideoCarouselProps } from '@/components/confluence/carousel/src/videoCarousel';
import { AnimatedJiraTourProps } from '@/components/product-demos/animated-jira-tour/src/animated-jira-tour';
import { BannerAlert, BannerProps } from '@/components/wpl/banner-alert/src/banner-alert';
import {
	ProductNavBar,
	ProductNavBarProps,
} from '@/components/wpl/product-navbar/src/product-navbar';
import { BgColors } from '@/libraries/design/components/link/src/link';
import { wacToken } from '@/libraries/design/foundations';
import { Layout, Row } from '@/libraries/wpl/foundations/src/grid';
import {
	ADG4CtaWithPolygonBackground,
	CtaWithPolygonBackgroundProps,
} from '@/patterns/adg4/cta-with-polygon-background/cta-with-polygon-background';
import { GlobalFooter } from '@/patterns/global-footer/src/global-footer';
import { PaidHeroProps } from '@/patterns/paid-hero/src/paid-hero';
import {
	PaidSocialProof,
	PaidSocialProofProps,
} from '@/patterns/paid-landings/paid-social-proof/src/paid-social-proof';
import { PaidTabsProps } from '@/patterns/paid-landings/paid-tabs/src/paid-tabs';
import { PatternThreeAlternatingImageTextProps } from '@/patterns/three-alternating-image-text/src/three-alternating-image-text';
import { PatternCTAMobileProps } from '@/patterns/wpl/cta-primary/src/cta-primary';
import {
	SimpleImageTextSide,
	SideBySideProps,
} from '@/patterns/wpl/simple-image-text-side/src/simple-image-text-side';
import { useLoggedInUser } from '@/utils/logged-in-user-provider';

import { experiments } from '../../../utils/experiment/manifest';
import { EnrollmentType, useExperiment } from '../../../utils/experiment/use-experiment';
import { useExperimentExposure } from '../../../utils/experiment/use-experiment-exposure';
import { ParentScrollTracking } from '../../../utils/scroll-tracking/parent-scroll-tracker';
import { PaidAlternatingImageText } from '../sections/paid-alternating-image-text';
import { PaidCtaSection } from '../sections/paid-cta-section';
import { PaidCustomHeroSection } from '../sections/paid-custom-hero-section';
import { PaidCustomSection } from '../sections/paid-custom-section';
import { PaidHeroSectionTemplate } from '../sections/paid-hero-section';
import {
	Animation,
	PaidHeroProps as PaidHeroSectionTemplateProps,
} from '../sections/paid-hero-section';
import { PaidHeroSectionTypes } from '../sections/paid-hero-section-types';
import { PaidTabsSection } from '../sections/paid-tabs-section';

export interface TabsProps extends PaidTabsProps {
	styles: object;
	landingType?: PaidLandingTypes;
}

export interface HeroProps extends PaidHeroProps {
	useNewHero?: boolean;
	showAnimatedHeroImage?: boolean;
	showStaticImage?: boolean;
	animatedSocialBanner?: boolean;
	darkSolidTheme?: boolean;
	animation?: Animation;
}

export type PaidLandingTypes =
	| 'jira-software'
	| 'jira-software-paid-ai'
	| 'jira-software-anchor'
	| 'jira-software-rebranded-anchor'
	| 'jira-service-management'
	| 'jira-service-management-anchor'
	| 'confluence-paid'
	| 'confluence-anchor'
	| 'confluence-rebranded-anchor';

export interface PaidLandingProps {
	landingType: PaidLandingTypes;
	pageVariant?: string;
	pageId?: string;
	showHorizontalRule?: boolean;
	productNavProps: ProductNavBarProps;
	bannerAlertProps?: BannerProps;
	heroProps: HeroProps;
	heroBgColor?: BgColors;
	hasRebrandedSignupFormStyles?: boolean;
	bannerProps?: SideBySideProps;
	tabsProps: TabsProps;
	customSection?: ReactNode;
	customHeroSection?: ReactNode;
	carouselHeroProps?: CarouselProps | VideoCarouselProps;
	alternatingTextAndImageProps?: Partial<PatternThreeAlternatingImageTextProps>;
	ctaProps?: PatternCTAMobileProps & { layout?: Layout };
	ctaWithPolyGonProps?: CtaWithPolygonBackgroundProps;
	testimonialProps?: PaidSocialProofProps;
	animatedJiraTourProps?: AnimatedJiraTourProps;
	hideNavigationBar?: boolean;
	heroImageSubheader?: string;
	resultSectionDivider?: ResultSectionDivderProps;
	auxiaResponse?: AuxiaResponse | null;
}

type ResultSectionDivderProps = {
	color: string;
	variation: SECTION_DIVIDER_VARIATION;
};

export const HorizontalRule: React.FC = () => (
	<Row layout="12">
		<div
			css={css({
				borderBlockEnd: `2px solid ${wacToken('color.neutral.light300')}`,
			})}
		/>
	</Row>
);

export const PaidLandingTemplate = ({
	landingType,
	pageId,
	showHorizontalRule,
	productNavProps,
	bannerAlertProps,
	heroProps,
	bannerProps,
	tabsProps,
	customSection = undefined,
	customHeroSection = undefined,
	carouselHeroProps,
	alternatingTextAndImageProps,
	ctaProps,
	ctaWithPolyGonProps,
	testimonialProps,
	animatedJiraTourProps,
	hideNavigationBar,
	heroImageSubheader,
	resultSectionDivider,
	hasRebrandedSignupFormStyles,
	heroBgColor = 'bgWhite',
	auxiaResponse,
}: PaidLandingProps) => {
	const { isUserLoggedIn } = useLoggedInUser();
	const [jiraFflExposureFired, fireJiraFflExposure] = useExperimentExposure(
		experiments.cfind6469FrictionlessFirstLookJiraV3,
	);

	const aiNativeTeamsExperiment = useExperiment(experiments.cfind6214WacLandingForAiNativeTeams);
	const isAiNativeTeamsEnrolled =
		productNavProps.productName === 'jira' &&
		aiNativeTeamsExperiment.type === EnrollmentType.ENROLLED;
	const showAiNativeTeamsUniformPage = isAiNativeTeamsEnrolled && !!aiNativeTeamsExperiment.value;
	const [aiNativeTeamsExposureFired, fireAiNativeTeamsExposure] = useExperimentExposure(
		experiments.cfind6214WacLandingForAiNativeTeams,
	);

	// Temporary solution to show animated hero only for logged out users on anchor pages
	// This can be removed once we implement a way to configure this via CMS
	const showAnimatedHero =
		!isUserLoggedIn && (pageId === 'paid-lp-jsw-features' || !!heroProps.useNewHero);

	const showStaticTestimonials = !heroProps.animatedSocialBanner;
	const hasBannerAlert: boolean = bannerAlertProps ? true : false;

	const defaultPaidArgs: PaidHeroSectionTemplateProps = {
		...heroProps,
		animatedJiraTourProps,
		subheader: heroProps.secondaryText,
		header: heroProps.headerText,
		hasRebrandedSignupFormStyles,
		heroImageSubheader,
		carouselHeroProps,
		hasBannerAlert: hasBannerAlert,
		heroBgColor: heroBgColor,
		auxiaResponse: auxiaResponse,
		pageId,
	};

	useEffect(() => {
		if (
			!jiraFflExposureFired &&
			productNavProps.productName === 'jira' &&
			pageId !== 'paid-lp-jsw-features'
		) {
			fireJiraFflExposure();
		}
	}, [jiraFflExposureFired, fireJiraFflExposure, productNavProps, pageId]);

	useEffect(() => {
		if (isAiNativeTeamsEnrolled && !aiNativeTeamsExposureFired) {
			fireAiNativeTeamsExposure();
		}
	}, [isAiNativeTeamsEnrolled, aiNativeTeamsExposureFired, fireAiNativeTeamsExposure]);

	if (showAiNativeTeamsUniformPage) {
		return <WacLandingForAiNativeTeamsWaePage />;
	}

	return (
		<main id="content">
			<ParentScrollTracking pageId={pageId} scrollTrackingEnabled={pageId ? true : false}>
				{!hideNavigationBar && <ProductNavBar {...productNavProps} />}
				{bannerAlertProps && (
					<BannerAlert {...bannerAlertProps} hasProductNavBar={!hideNavigationBar} />
				)}
				{customHeroSection ? (
					<PaidCustomHeroSection customHeroSection={customHeroSection} />
				) : (
					<PaidHeroSectionTypes
						darkSolidTheme={heroProps.darkSolidTheme}
						landingType={landingType}
						heroComponent={<PaidHeroSectionTemplate {...defaultPaidArgs} />}
						showAnimatedHero={showAnimatedHero}
						showNewHero={defaultPaidArgs.useNewHero}
					/>
				)}
				<PaidTabsSection {...tabsProps} />
				{customSection && <PaidCustomSection customSection={customSection} />}
				{showHorizontalRule && <HorizontalRule />}
				{bannerProps && <SimpleImageTextSide {...bannerProps} />}
				{resultSectionDivider && (
					<div
						css={css({
							marginBlockEnd: '-80px',
						})}
					>
						<Adg4SectionDivider
							bgColor={resultSectionDivider.color}
							variation={resultSectionDivider.variation}
						/>
					</div>
				)}
				<PaidAlternatingImageText
					landingType={landingType}
					alternatingImageTextProps={alternatingTextAndImageProps}
				/>
				{ctaProps && <PaidCtaSection ctaProps={ctaProps} landingType={landingType} />}
				{ctaWithPolyGonProps && (
					<>
						<div
							css={
								resultSectionDivider &&
								css({
									backgroundColor: resultSectionDivider?.color,
									marginBlockStart: '-80px',
									height: '200px',
								})
							}
						/>
						<div
							css={
								resultSectionDivider &&
								css({
									marginBlockStart: '-200px',
								})
							}
						>
							<ADG4CtaWithPolygonBackground {...ctaWithPolyGonProps} />
						</div>
					</>
				)}
				{showStaticTestimonials && testimonialProps && <PaidSocialProof {...testimonialProps} />}
				<GlobalFooter />
			</ParentScrollTracking>
		</main>
	);
};

const WacLandingForAiNativeTeamsWaePage = () => {
	const { composition } = useWAEContext();

	return <UniformEntryPage composition={composition as RootComponentInstance} />;
};
